<template>
    <div class="scorm-player d-flex flex-column w-100">
        <div
            v-if="!ready"
            class="scorm-loading d-flex justify-content-center align-items-center"
        >
            <div class="text-center">
                <i class="mdi mdi-loading mdi-spin mdi-36px text-primary"></i>
                <p class="mt-2 mb-0">{{ $t('common.loading') }}</p>
            </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mx-3 mt-3">
            {{ errorMessage }}
        </div>

        <iframe
            v-show="ready && !errorMessage"
            ref="scormFrame"
            :src="launchUrl"
            class="scorm-frame w-100"
            frameborder="0"
            allowfullscreen
            scrolling="yes"
            @load="onFrameLoad"
        ></iframe>
    </div>
</template>

<script>
export default {
    emits: ['input', 'progress', 'update:modelValue'],
    name: 'ScormPlayer',

    props: {
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        contentId: {
            type: Number,
            required: true,
        },
        lessonId: {
            type: Number,
            required: true,
        },
        lesson:   { type: Object, default: () => ({}) },
        user:     { type: Object, default: () => ({}) },
        parents:  { type: Object, default: () => ({}) },
        isPublic: { type: Boolean, default: false },
    },

    data() {
        return {
            ready:        false,
            initializing: false,
            attemptId:    null,
            launchUrl:    null,
            localCmi:     {},
            dirtyKeys:    new Set(),
            flushTimer:   null,
            lastError:    '0',
            errorMessage: null,
        };
    },

    computed: {
        storeLessonContent() {
            return this.$store.getters.LOADED_CONTENT;
        },
    },

    watch: {
        // contentId muda APÓS o parent concluir LOAD_CONTENT, então o store já tem o dado novo
        contentId() {
            this.teardown();
            this.initFromStore();
        },
    },

    methods: {
        initFromStore() {
            const data = this.storeLessonContent;
            if (data && data.type === 'scorm') {
                this.initSession(data);
            }
        },

        async initSession(packageData) {
            if (this.initializing) return;
            this.initializing = true;
            this.ready        = false;
            this.errorMessage = null;
            this.attemptId    = null;
            this.localCmi     = {};
            this.dirtyKeys.clear();

            const entrySco = packageData.entry_sco
                || (packageData.scos && packageData.scos[0]);

            if (!entrySco) {
                this.errorMessage = this.$t('lesson.scorm.no_entry_sco');
                return;
            }

            try {
                const res = await window.axios.post('/api/app/scorm/attempt/start', {
                    scorm_package_id:  packageData.scorm_package_id,
                    scorm_sco_id:      entrySco.id,
                    lesson_content_id: packageData.id_lesson_content,
                });

                this.attemptId = res.data.attempt_id;
                this.localCmi  = Object.assign({}, res.data.cmi || {});

                this.setupScormApi();

                this.launchUrl = packageData.launch_url;
                this.ready     = true;
            } catch (err) {
                console.error('[SCORM] initSession error:', err);
                this.errorMessage = this.$t('lesson.scorm.init_error');
            } finally {
                this.initializing = false;
            }
        },

        setupScormApi() {
            const vm = this;

            const api12 = {
                LMSInitialize:    ()    => { vm.apiInitialize(); return 'true'; },
                LMSGetValue:      (k)   => vm.apiGetValue(k),
                LMSSetValue:      (k,v) => { vm.apiSetValue(k, v); return 'true'; },
                LMSCommit:        ()    => { vm.apiCommit(); return 'true'; },
                LMSFinish:        ()    => { vm.apiFinish(); return 'true'; },
                LMSGetLastError:  ()    => vm.lastError,
                LMSGetErrorString:(c)   => vm.getErrorString(c),
                LMSGetDiagnostic: (c)   => String(c),
            };

            const api2004 = {
                Initialize:    api12.LMSInitialize,
                GetValue:      api12.LMSGetValue,
                SetValue:      api12.LMSSetValue,
                Commit:        api12.LMSCommit,
                Terminate:     api12.LMSFinish,
                GetLastError:  api12.LMSGetLastError,
                GetErrorString:api12.LMSGetErrorString,
                GetDiagnostic: api12.LMSGetDiagnostic,
            };

            window.API         = api12;
            window.API_1484_11 = api2004;
        },

        apiInitialize() {
            if (!this.attemptId) return;
            window.axios
                .post(`/api/app/scorm/${this.attemptId}/initialize`)
                .catch(err => console.error('[SCORM] initialize error:', err));
        },

        apiGetValue(key) {
            const val = this.localCmi[key];
            return val !== undefined ? String(val) : '';
        },

        apiSetValue(key, value) {
            this.localCmi[key] = value;
            this.dirtyKeys.add(key);
            this.scheduleFlush();
        },

        scheduleFlush() {
            if (this.flushTimer) clearTimeout(this.flushTimer);
            this.flushTimer = setTimeout(() => this.flushToBackend(), 3000);
        },

        async flushToBackend() {
            if (this.flushTimer) {
                clearTimeout(this.flushTimer);
                this.flushTimer = null;
            }
            if (!this.attemptId || this.dirtyKeys.size === 0) return;

            const values = [];
            for (const key of this.dirtyKeys) {
                values.push({ key, value: this.localCmi[key] ?? '' });
            }
            this.dirtyKeys.clear();

            try {
                await window.axios.post(`/api/app/scorm/${this.attemptId}/commit`, { values });
            } catch (err) {
                console.error('[SCORM] flush error:', err);
            }
        },

        apiCommit() {
            this.flushToBackend();
        },

        async apiFinish() {
            await this.flushToBackend();
            if (!this.attemptId) return;

            try {
                const res = await window.axios.post(`/api/app/scorm/${this.attemptId}/terminate`);
                if (res.data.progress !== undefined) {
                    this.$emit('progress', { progress: res.data.progress });
                }
                this.attemptId = null;
            } catch (err) {
                console.error('[SCORM] terminate error:', err);
            }
        },

        onFrameLoad() {
            // iframe carregado — nenhuma ação adicional necessária
        },

        teardown() {
            if (this.flushTimer) {
                clearTimeout(this.flushTimer);
                this.flushTimer = null;
            }
            if (window.API)         delete window.API;
            if (window.API_1484_11) delete window.API_1484_11;
            this.ready     = false;
            this.launchUrl = null;
        },

        getErrorString(code) {
            const strings = {
                '0':   'No Error',
                '101': 'General Exception',
                '201': 'Invalid argument error',
                '202': 'Element cannot have children',
                '203': 'Element not an array',
                '301': 'Not initialized',
                '401': 'Not implemented error',
                '402': 'Invalid set value, element is read only',
                '403': 'Element is write only',
                '404': 'Incorrect Data Type',
            };
            return strings[String(code)] || 'Unknown Error';
        },
    },

    mounted() {
        this.initFromStore();
    },

    beforeUnmount() {
        if (this.flushTimer) clearTimeout(this.flushTimer);
        // Não aguardar apiFinish pois beforeDestroy é síncrono;
        // LMSFinish deveria ter sido chamado pelo próprio conteúdo SCORM.
        this.teardown();
    },
};
</script>

<style scoped>
.scorm-player {
    flex: 1;
    min-height: 0;
}

.scorm-loading {
    min-height: 200px;
}

.scorm-frame {
    flex: 1;
    min-height: 0;
    border: none;
}
</style>
